import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { from, switchMap, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { DocumentType } from '../../shared/models/document.models';

@Injectable({ providedIn: 'root' })
export class DocumentService {
  private http = inject(HttpClient);
  private base = `${environment.apiUrl}/api`;
  private readonly sasUrl = new URL(environment.azureStorageSasUrl);
  private readonly urlPrefix = environment.azureStorageUrlPrefix;

  getDocumentTypes() {
    return this.http.get<DocumentType[]>(`${this.base}/document-types`);
  }

  getReadUrl(documentUrl: string) {
    return this.http
      .get<{ sasToken: string }>(`${this.base}/blob`)
      .pipe(map(({ sasToken }) => `${this.urlPrefix}/${documentUrl}?${sasToken}`));
  }

  deleteDocument(id: string) {
    return this.http.delete<void>(`${this.base}/documents/${id}`);
  }

  uploadDocument(payload: {
    entityId: string;
    personId: string;
    documentTypeId: string;
    file: File;
    type: 'incidents' | 'investigations';
  }) {
    const ts = Date.now();
    const blobPath = `${payload.type}/${payload.entityId}/${ts}-${payload.file.name}`;
    const uploadUrl = `${this.urlPrefix}/${blobPath}${this.sasUrl.search}`;

    const isInvestigation = payload.type === 'investigations';

    return from(
      fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': payload.file.type || 'application/octet-stream',
        },
        body: payload.file,
      }).then((res) => {
        if (!res.ok) throw new Error(`Azure upload failed: ${res.status}`);
      }),
    ).pipe(
      switchMap(() =>
        this.http.post<void>(`${this.base}/documents`, {
          incidentId: isInvestigation ? null : payload.entityId,
          investigationId: isInvestigation ? payload.entityId : null,
          personId: payload.personId || null,
          documentTypeId: payload.documentTypeId,
          documentUrl: blobPath,
          name: payload.file.name,
        }),
      ),
    );
  }
}
